import * as React from 'react'
import { Button } from '~/client/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '~/client/components/ui/avatar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '~/client/components/ui/dropdown-menu'
import EventsDropdown from '~/client/components/event/EventsDropdown'
import SharedListsDropdown from '~/client/components/shareable-list/SharedListsDropdown'
import ProfileSheet from '~/client/components/user/ProfileSheet'
import { Link, useNavigate, useLocation } from '@tanstack/react-router'
import { useAuthStore, useEffectiveUserDetail } from '~/client/common/stores/auth'
import { RevelStreetLogoHeaderIcon, BasilAddSolidIcon } from '~/client/components/icons'
import { LogOut, KeyRound, Tags } from 'lucide-react'
import { AddEventToSpaceDialog } from '~/client/components/event/EventCreationDialogs'
import { useEvents } from '~/client/event/use-events.hook'
import { useShareableLists } from '~/client/shareable-list/use-shareable-lists.hook'
import { MyEventsCredenza } from '~/client/components/event/MyEventsCredenza'
import { MySharedListsCredenza } from '~/client/components/shareable-list/MySharedListsCredenza'
import { ShareableListDialog } from '~/client/components/shareable-list/ShareableListDialog'
import { useState } from 'react'
import { useFiltersStore } from '~/client/common/stores/filters'
import { useListingsStore } from '~/client/common/stores/listings'
import { extractDateOnlyYYYYMMDD } from '~/shared/common/date'
import { DateTime } from 'luxon'
import { hasFeature, FeatureFlag } from '~/client/common/features'
import { hasVenueTagAdminAccess } from '~/shared/venue-tag/access'

function formatCompactDateOnlyFromUnknown(value: unknown): string | undefined {
  const dateOnly = extractDateOnlyYYYYMMDD(value)
  return dateOnly ? DateTime.fromISO(dateOnly, { zone: 'utc' }).toFormat('M/d/yyyy') : undefined
}

interface AppHeaderProps {
  showVenueGroupSelector?: boolean
  showEvents?: boolean
  showAccountMenu?: boolean
}

export default function AppHeader({ showEvents = true, showAccountMenu = true }: AppHeaderProps) {
  const logout = useAuthStore((s) => s.logout)
  const userDetail = useEffectiveUserDetail()
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const location = useLocation()
  const resetFilters = useFiltersStore((s) => s.reset)
  const resetListingsFilters = useListingsStore((s) => s.resetFilters)
  const [isCreateEventDialogOpen, setIsCreateEventDialogOpen] = useState(false)
  const [isProfileSheetOpen, setIsProfileSheetOpen] = useState(false)
  const [isMyEventsCredenzaOpen, setIsMyEventsCredenzaOpen] = useState(false)
  const [isMySharedListsCredenzaOpen, setIsMySharedListsCredenzaOpen] = useState(false)
  const [isCreateShareableListDialogOpen, setIsCreateShareableListDialogOpen] = useState(false)

  const hasCollectionsFeature = hasFeature(userDetail, FeatureFlag.COLLECTIONS)
  const hasVenueTagAdminFeature = hasVenueTagAdminAccess(
    userDetail?.active_entity?.features,
    userDetail?.membership_type,
  )

  async function handleLogout() {
    await logout()
  }

  function handleLogoClick(e: React.MouseEvent<HTMLAnchorElement>) {
    if (location.pathname === '/' || location.pathname === '/') {
      e.preventDefault()
      resetFilters()
      resetListingsFilters()
    }
  }

  const { data: events, isLoading: isEventsLoading } = useEvents({ page_size: 200 })
  const { data: sharedLists, isLoading: isSharedListsLoading } = useShareableLists({ enabled: hasCollectionsFeature })
  const sharedListOptions = (sharedLists || []).map((l) => ({
    id: String(l.id),
    title: l.name || 'Untitled Collection',
    spaceCount: l.memberships?.length ?? 0,
    slug: l.slug,
    venueGroupName: l.venueGroupName ?? undefined,
    lastUpdatedAt: formatCompactDateOnlyFromUnknown(l.lastUpdatedAt) ?? undefined,
  }))

  const eventOptions = (events || []).map((e) => {
    const firstDate = e.event_space_list_dates?.[0]?.start_date
    const rawDate = formatCompactDateOnlyFromUnknown(firstDate)
    return {
      id: String(e.id),
      title: e.name,
      venueGroupName: e.venue_group_name ?? undefined,
      dateLabel: rawDate ?? undefined,
      ownerName: e.owner_name ? `Owner: ${e.owner_name}` : undefined,
      slug: e.slug,
    }
  })

  const displayName = userDetail ? `${[userDetail.first_name, userDetail.last_name].filter(Boolean).join(' ')}`.trim() || userDetail.email || 'User' : 'User'
  const organizationName = userDetail?.active_entity?.name || ''
  const avatarInitials = userDetail ? (userDetail.first_name?.[0] || '') + (userDetail.last_name?.[0] || '') || userDetail.email || '' : ''
  const avatarUrlRaw: unknown = user?.user_metadata?.avatar_url
  const avatarUrl = typeof avatarUrlRaw === 'string' ? avatarUrlRaw : undefined
  const avatarFallback = avatarInitials.slice(0, 2).toUpperCase() || 'U'

  return (
    <div className="bg-white border-b border-gray-200 h-16 px-8 py-3 flex items-center justify-between w-full">
      <div className="flex items-center gap-4">
        <Link to="/" className="shrink-0" onClick={handleLogoClick}>
          <RevelStreetLogoHeaderIcon className="h-10 w-auto text-[#18003D]" />
        </Link>
        {/* City dropdown removed per Section 5.1 — city context is now
            captured in the event creation form on the homepage. */}
      </div>

      {/* Desktop header actions */}
      <div className="hidden md:flex items-center gap-3">
        {showEvents && (
          <>
            {hasCollectionsFeature && (
              <SharedListsDropdown
                options={sharedListOptions}
                isLoading={isSharedListsLoading}
                onSelect={(list) => {
                  void navigate({ to: '/collection/$slug', params: { slug: list.slug } })
                }}
                onCreateNew={() => setIsCreateShareableListDialogOpen(true)}
              />
            )}
            <EventsDropdown
              options={eventOptions}
              isLoading={isEventsLoading}
              onSelect={(ev) => {
                void navigate({ to: '/events/$eventSlug', params: { eventSlug: ev.slug }, search: { tab: 'shortlist' } })
              }}
              onCreateNew={() => setIsCreateEventDialogOpen(true)}
            />

            <Button variant="revel" size="revel40" className="pl-3 pr-2" onClick={() => setIsCreateEventDialogOpen(true)}>
              <span className="text-white">Create Event</span>
              <BasilAddSolidIcon variant="white" className="size-6" />
            </Button>
          </>
        )}

        {showAccountMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Avatar className="h-10 w-10 ring-1 ring-[#8a3dff] cursor-pointer">
                <AvatarImage src={avatarUrl} alt="profile" />
                <AvatarFallback>
                  {avatarFallback}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[320px] p-0 rounded-[12px] border border-[#e4e1e9] overflow-hidden">
              {/* User header */}
              <div className="px-4 py-3 flex items-center gap-3">
                <Avatar className="h-10 w-10 ring-[3px] ring-[#8a3dff]">
                  <AvatarImage src={avatarUrl} alt="profile" />
                  <AvatarFallback>
                    {avatarFallback}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col leading-[1.2]">
                  <p className="font-medium text-[#55534e] text-[14px]">{displayName}</p>
                  <p className="text-[#b6aec1] text-[12px]">{organizationName}</p>
                </div>
              </div>
              <div className="h-px bg-[#e4e1e9]" />

              {/* Menu items */}
              <div className="">
                {/*   <DropdownMenuItem className="px-4 py-3 text-[14px] text-[#55534e] hover:bg-[rgba(249,197,101,0.08)] focus:bg-[rgba(249,197,101,0.08)] cursor-pointer">
                  <span className="flex-1">Early Access</span>
                  <Crown className="size-5 text-[#f9c565]" fill="#f9c565" />
                </DropdownMenuItem> */}

                {hasVenueTagAdminFeature && (
                  <DropdownMenuItem
                    onSelect={() => {
                      void navigate({ to: '/tag-settings' })
                    }}
                    className="px-4 py-3 text-[14px] text-[#55534e] hover:text-[#55534e] hover:bg-[#55534e]/10 cursor-pointer"
                  >
                    <span className="flex-1">Tag Settings</span>
                    <Tags className="size-5 text-[#9b9b9a]" />
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem
                  onSelect={() => {
                    void navigate({ to: '/change-password' })
                  }}
                  className="px-4 py-3 text-[14px] text-[#55534e] hover:text-[#55534e] hover:bg-[#55534e]/10 cursor-pointer"
                >
                  <span className="flex-1">Change Password</span>
                  <KeyRound className="size-5 text-[#9b9b9a]" />
                </DropdownMenuItem>
              </div>

              <div className="border-t border-[#e4e1e9]" />

              {/* Logout */}
              <DropdownMenuItem
                onClick={() => {
                  void handleLogout()
                }}
                className="px-4 py-3 text-[14px] text-[#d04b46] hover:text-[#d04b46] hover:bg-[#FDF8F8] cursor-pointer"
              >
                <LogOut className="size-5 text-[#d04b46]" />
                <span className="ml-1 text-[#d04b46]">Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Mobile header actions: Avatar opens ProfileSheet */}
      {showAccountMenu && (
        <div className="flex md:hidden items-center">
          <button onClick={() => setIsProfileSheetOpen(true)}>
            <Avatar className="h-10 w-10 ring-1 ring-[#8a3dff] cursor-pointer">
              <AvatarImage src={avatarUrl} alt="profile" />
              <AvatarFallback>
                {avatarFallback}
              </AvatarFallback>
            </Avatar>
          </button>
        </div>
      )}

      <ProfileSheet
        open={isProfileSheetOpen}
        onOpenChange={setIsProfileSheetOpen}
        onCreateEvent={() => setIsCreateEventDialogOpen(true)}
        onMyEvents={() => setIsMyEventsCredenzaOpen(true)}
        onMySharedLists={() => setIsMySharedListsCredenzaOpen(true)}
        onSubscribe={() => console.log('Subscribe to plan')}
        onChangePassword={() => {
          void navigate({ to: '/change-password' })
        }}
      />

      <MySharedListsCredenza
        open={isMySharedListsCredenzaOpen}
        onOpenChange={setIsMySharedListsCredenzaOpen}
        lists={sharedListOptions}
        isLoading={isSharedListsLoading}
        onCreateList={() => setIsCreateShareableListDialogOpen(true)}
      />

      <MyEventsCredenza
        open={isMyEventsCredenzaOpen}
        onOpenChange={setIsMyEventsCredenzaOpen}
        events={eventOptions}
        isLoading={isEventsLoading}
        onCreateEvent={() => setIsCreateEventDialogOpen(true)}
      />



      <AddEventToSpaceDialog startStep="create-event-form" open={isCreateEventDialogOpen} onOpenChange={() => setIsCreateEventDialogOpen(false)} shouldAddSpaceOnCreate={false} />
      <ShareableListDialog
        open={isCreateShareableListDialogOpen}
        onOpenChange={setIsCreateShareableListDialogOpen}
        initialStep="create"
      />
    </div>
  )
}
